import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GetPaymentsNewResponse } from '@vy/api-definition';
import type { ItemOf } from '@vy/utils';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { saveAs } from 'file-saver';
import type { VyDate } from '@vy/utils';
import { vyDate } from '@vy/utils';
import { useCallback } from 'react';

import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { useNonPagedPaymentsFromParams } from '../../../../hooks/requests/use-payments-new/use-non-paged-payments-from-params';
import { runMutation } from '../../../../utils/vy-mutation/vy-mutation';
import { generateCSV } from '../payments-page-utils/payments-page-generate-csv';

type PaymentsFromSelection = ItemOf<GetPaymentsNewResponse['items']>[];

type UsePaymentsActionsProps = {
  selectedPayments: PaymentsFromSelection;
  apiRef: React.MutableRefObject<GridApiPro | null>;
};

export const usePaymentsActions = ({ selectedPayments, apiRef }: UsePaymentsActionsProps) => {
  const allPaymentsMutation = useNonPagedPaymentsFromParams();
  const [fromDay] = useFromDayParam();
  const [toDay] = useToDayParam();

  const getPaymentsToExport = useCallback(
    async (exportAll: boolean, selectedRows: PaymentsFromSelection) => {
      if (!exportAll && !isEmptyArray(selectedRows)) {
        return selectedRows;
      }

      if (!exportAll && !isEmptyArray(selectedPayments)) {
        return selectedPayments;
      }

      const allPaymentsResponse = await runMutation(allPaymentsMutation, undefined, {
        pending: 'Downloading all payments...',
        success: 'All payments downloaded',
        error: 'Failed to download all payments',
      });

      return allPaymentsResponse?.items ?? [];
    },
    [selectedPayments, allPaymentsMutation],
  );

  const handleExportSelectedPayments = useCallback(async () => {
    const selectedRows = apiRef.current?.getSelectedRows() ?? new Map();
    const paymentsFromSelection = Array.from(selectedRows.values()) as PaymentsFromSelection;
    const shouldExportAll = isEmptyArray(paymentsFromSelection) && isEmptyArray(selectedPayments);
    const paymentsToExport = await getPaymentsToExport(shouldExportAll, paymentsFromSelection);

    let visibleFields: string[] | undefined;
    if (isNotNullOrUndefined(apiRef?.current)) {
      visibleFields = apiRef.current.getVisibleColumns().map((column) => column.field);
    }

    const csvContent = generateCSV(paymentsToExport ?? [], visibleFields);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    const fileName = buildExportFileName(fromDay, toDay);
    saveAs(blob, `${fileName}.csv`);
  }, [selectedPayments, apiRef, getPaymentsToExport, fromDay, toDay]);

  return {
    handleExportSelectedPayments,
  };
};

function buildExportFileName(fromDay: VyDate | null, toDay: VyDate | null): string {
  if (isNotNullOrUndefined(fromDay) && isNotNullOrUndefined(toDay)) {
    return `Payments ${fromDay.format('MM-DD-YYYY')} to ${toDay.format('MM-DD-YYYY')}`;
  }

  return vyDate().format('MMMM, DD, YYYY');
}
